import { getWallPath } from "building-generation/elements/walls/wall";
import type { GeneratedPlanAreaMeasurement } from "building-types/entities/measurementTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { Porch } from "building-types/porches/porch";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import { FloorAreaTakeoffType, SupplementalArea } from "building-types/rooms/takeoffTypes";
import type { FloorAreaTakeOff, FloorAreaTakeoffs, LevelFloorAreaTakeoff } from "building-types/rooms/takeoffTypes";
import type { PlanSettings } from "building-types/settings/planSettings";
import type { WallIndex } from "building-types/walls/wallIndex";
import type { WallMassSystem, WallSystem } from "building-types/walls/wallSytemTypes";
import type { RWTable, Table } from "common/data/table";
import { feetSquaredFromInchesSquared, feetSquaredNew, inchesSquaredNew, numRound } from "common/utils/nominalNumbers";
import { roomsOnLevel } from "doc/rooms/roomsOnLevel";
import { uniqueRoomSetsTables } from "doc/rooms/uniqueRoomSetsTables";
import { classifyContainmentVec2PolyLine2, Vec2PolyLine2ContainmentClass } from "geom/containment";
import { offsetPolyLine2VariableStep } from "geom/offset";
import type { PlanarGraphLoops } from "geom/planarGraph";
import { planarGraphTracePerimeter, planarGraphVertexStripToPolyLine2 } from "geom/planarGraph";
import { polygonUnion } from "geom/polygon";
import type { PolygonPlanarGraph } from "geom/polygonPlanarGraph";
import {
  polyLine2Concat,
  polyLine2Copy,
  polyLine2EdgeEval,
  polyLine2IsClosed,
  polyLine2MergeCoincidentVertices,
  polyLine2SetCCW,
  polyLine2SetCW,
} from "geom/polyLine2";
import { polygonArea } from "geom/polyLine2Polygon";
import type { PolyLine2, PolyLine2MultiPolygon } from "geom/polyLine2Types";
import { getRoomsInFloorAreaTakeoff } from "../elements/floorAreaTakeoffs/getIncludedAndExcludedRooms";
import { makePerimeterEdgeOffsetList } from "../elements/floorAreaTakeoffs/perimeterEdgeOffsets";
import { makeFloorAreaTakeOffConfigs } from "../elements/floorAreaTakeoffs/takeoffConfig";
import { groundLevelId } from "../elements/level/levelTools";
import { applyMeasurements } from "../elements/measurement/measurement";
import { getPlanarGraphFaces } from "../utils/planarGraphPerimeters";

export function makeFloorAreaTakeoffs(
  planarGraphs: Table<PolygonPlanarGraph>,
  walls: Table<WallSystem>,
  wallMasses: Table<WallMassSystem>,
  rooms: Table<Room>,
  roomsByLevel: Table<string[]>,
  levels: Table<Level>,
  roomLevelIndices: Table<number>,
  porches: Table<Porch>,
  settings: PlanSettings,
  measurements: Table<GeneratedPlanAreaMeasurement>,
): {
  totalFloorArea: number;
  floorAreaTakeoffs: FloorAreaTakeoffs;
} {
  const takeoffConfigs = makeFloorAreaTakeOffConfigs(settings);
  const supplementalOutlines = makeSupplementalOutlineTable(porches);

  const { roomSets, roomIdToRoomSetIndexLookup } = uniqueRoomSetsTables(levels, wallMasses);

  const takeoffs: FloorAreaTakeoffs = {};

  for (const key in takeoffConfigs) {
    const levelFloorAreas: RWTable<LevelFloorAreaTakeoff> = {};
    const takeoffConfig = takeoffConfigs[key];

    let totalFloorArea = 0;
    let totalFloorAreaExact = 0;

    for (const levelId in levels) {
      const levelRooms = roomsOnLevel(levelId, rooms, roomsByLevel);
      if (levelRooms.length === 0) {
        levelFloorAreas[levelId] = {
          floorArea: feetSquaredNew(0),
          floorAreaExact: feetSquaredNew(0),
          floorAreaPolygons: [],
          roomHandles: [],
        };
        continue;
      }

      const planarGraph = planarGraphs[levelId];
      const levelWalls = walls[levelId];
      const roomSetsPerLevel = roomSets.filter((x) => x.levelId === levelId);
      const wallIndex = levelWalls.wallIndex;
      const floorIndex = levels[levelId].floorIndex;

      const { includedRoomIds, roomBoundaryTable } = getRoomsInFloorAreaTakeoff(
        levelRooms,
        roomSetsPerLevel,
        roomIdToRoomSetIndexLookup,
        takeoffConfig,
        floorIndex,
      );

      const { roomsPerimeter: outerLoopRooms, roomsInterior: innerLoopRooms } =
        getOuterAndInnerLoopExcludedRoomsFromLevelAreaPolygon(levelRooms, wallIndex, includedRoomIds, rooms);

      const nonAreaFaces = getPlanarGraphFacesForExcludedRooms(
        rooms,
        roomLevelIndices,
        roomsByLevel[levelId],
        planarGraph,
        outerLoopRooms,
      );

      const areaPerimeters = planarGraphTracePerimeter(planarGraph.planarGraph, nonAreaFaces);

      // boundary rules go here to create the offset
      const offsetList: number[][] = makePerimeterEdgeOffsetList(
        areaPerimeters,
        levelWalls,
        takeoffConfig,
        includedRoomIds,
        roomBoundaryTable,
      );

      let floorAreaPolygons: PolyLine2MultiPolygon = [];

      const mergedOuterLoops = makeMultioffsetPerimeterPolyLines(areaPerimeters, planarGraph, offsetList, true);

      if (mergedOuterLoops.length > 0) {
        floorAreaPolygons.push(...mergedOuterLoops.map((x) => [x]));
      }

      const outerLoopCount = Object.keys(mergedOuterLoops).length;
      const innerLoopCount = Object.keys(innerLoopRooms).length;

      if (outerLoopCount > 0 && innerLoopCount > 0) {
        const excludedFromInnerLoops: { [id: string]: boolean } = {};
        for (const rm of levelRooms) {
          if (!innerLoopRooms[rm.id]) {
            excludedFromInnerLoops[rm.id] = true;
          }
        }

        const { roomsPerimeter } = getOuterAndInnerLoopExcludedRoomsFromLevelAreaPolygon(
          levelRooms,
          wallIndex,
          innerLoopRooms,
          rooms,
        );

        const nonAreaFaces2 = getPlanarGraphFacesForExcludedRooms(
          rooms,
          roomLevelIndices,
          roomsByLevel[levelId],
          planarGraph,
          roomsPerimeter,
        );

        const areaPerimeters2 = planarGraphTracePerimeter(planarGraph.planarGraph, nonAreaFaces2);

        const offsetList2 = makePerimeterEdgeOffsetList(
          areaPerimeters2,
          levelWalls,
          takeoffConfig,
          includedRoomIds,
          roomBoundaryTable,
        );

        const mergedInnerLoops = makeMultioffsetPerimeterPolyLines(areaPerimeters2, planarGraph, offsetList2, false);

        // nest inner loop inside of containing outer loop
        for (const mergedInnerLoop of mergedInnerLoops) {
          const [x, y] = polyLine2EdgeEval(mergedInnerLoop, 0, 0);
          for (const index of Object.keys(mergedOuterLoops)) {
            const mergedOuterLoop = mergedOuterLoops[index];

            const containment = classifyContainmentVec2PolyLine2(x, y, mergedOuterLoop);

            if (containment === Vec2PolyLine2ContainmentClass.INSIDE) {
              floorAreaPolygons[index].push(mergedInnerLoop);
              break;
            }
          }
        }
      }

      // supplemental areas only supported on first floor currently
      if (takeoffConfig.includeSupplementalAreas && levelId === groundLevelId(levels)) {
        for (const key in takeoffConfig.includeSupplementalAreas) {
          const outlines: PolyLine2[] = supplementalOutlines[key];
          if (outlines.length > 0) {
            for (const outline of outlines) {
              floorAreaPolygons.push([polyLine2Copy(outline)]);
            }
          }
        }
      }

      // select room measurements that are on this level and report to the current takeoff type
      const roomMeasurements = Object.values(measurements).filter(
        (measure) => levelRooms.some((r) => r.id === measure.parentId) && measure.reportCategories[key],
      );

      floorAreaPolygons = applyMeasurements(floorAreaPolygons, roomMeasurements);

      const floorAreaInchesSquared = inchesSquaredNew(floorAreaPolygons.reduce((acc, x) => acc + polygonArea(x), 0));
      const floorArea = feetSquaredFromInchesSquared(floorAreaInchesSquared);
      const floorAreaRounded = numRound(floorArea);

      totalFloorArea += floorAreaRounded;
      totalFloorAreaExact += floorArea;

      levelFloorAreas[levelId] = {
        floorArea: floorAreaRounded,
        floorAreaExact: floorArea,
        floorAreaPolygons,
        roomHandles: levelRooms.filter((room) => includedRoomIds[room.id]).map((room) => room.roomHandle),
      };
    }

    takeoffs[key] = {
      totalFloorArea,
      totalFloorAreaExact,
      levelFloorAreas,
    } as FloorAreaTakeOff;
  }

  return {
    totalFloorArea: takeoffs[FloorAreaTakeoffType.TOTAL_FLOOR_AREA]?.totalFloorArea ?? 0,
    floorAreaTakeoffs: takeoffs,
  };
}

function makeSupplementalOutlineTable(porches: Table<Porch>) {
  const supplementalOutlines: {
    [key in SupplementalArea]?: PolyLine2[];
  } = {
    [SupplementalArea.FRONT_PORCH_COLONNADE]: [],
    [SupplementalArea.REAR_PORCH_COLONNADE]: [],
  };

  for (const porch of Object.values(porches)) {
    if (porch.roomType === RoomType.FRONT_PORCH) {
      for (const edgeOutline of porch.porchColonnadeSupplementalAreaOutlines) {
        const polys = supplementalOutlines[SupplementalArea.FRONT_PORCH_COLONNADE] || [];
        polys.push(edgeOutline);
        supplementalOutlines[SupplementalArea.FRONT_PORCH_COLONNADE] = polys;
      }
    } else if (porch.roomType === RoomType.REAR_PORCH) {
      for (const edgeOutline of porch.porchColonnadeSupplementalAreaOutlines) {
        const polys = supplementalOutlines[SupplementalArea.REAR_PORCH_COLONNADE] || [];
        polys.push(edgeOutline);
        supplementalOutlines[SupplementalArea.REAR_PORCH_COLONNADE] = polys;
      }
    }
  }

  return supplementalOutlines;
}

function makeMultioffsetPerimeterPolyLines(
  areaPerimeters: PlanarGraphLoops,
  planarGraph: PolygonPlanarGraph,
  offsetList: number[][],
  isOuter: boolean,
) {
  const perimeters: PolyLine2[] = [];

  for (let j = 0, jl = areaPerimeters.faceVertices.length; j < jl; j++) {
    let perimeter = planarGraphVertexStripToPolyLine2(planarGraph.planarGraph, areaPerimeters.faceVertices[j]);
    perimeter = offsetPolyLine2VariableStep(perimeter, offsetList[j]).pl;
    if (!polyLine2IsClosed(perimeter)) {
      perimeter = polyLine2Concat(perimeter, [perimeter[0], perimeter[1]]);
    }
    polyLine2MergeCoincidentVertices(perimeter);
    perimeters.push(polyLine2Copy(perimeter));
  }

  const mergedPerimeters: PolyLine2[] = [];
  if (perimeters.length > 1) {
    mergedPerimeters.push(...polygonUnion(perimeters.map((x) => [x])).map((x) => x[0]));
  } else {
    if (perimeters[0]) {
      mergedPerimeters.push(perimeters[0]);
    }
  }

  return isOuter ? mergedPerimeters.map((x) => polyLine2SetCW(x)) : mergedPerimeters.map((x) => polyLine2SetCCW(x));
}

function getOuterAndInnerLoopExcludedRoomsFromLevelAreaPolygon(
  roomsOnLevel: Room[],
  wallIndex: WallIndex,
  includedRoomIds: { [roomId: string]: boolean },
  rooms: Table<Room>,
) {
  const roomsPerimeter: { [id: string]: boolean } = {};
  const roomsInterior: { [id: string]: boolean } = {};

  const onTrueExterior: { [id: string]: boolean } = {};
  const adjacentRoomGroups: { [id: string]: boolean }[] = [];

  const visitedRooms: { [id: string]: boolean } = {};

  for (const room of roomsOnLevel) {
    const { id } = room;

    if (includedRoomIds[id] || visitedRooms[id]) {
      continue;
    }

    const currentGroup: { [id: string]: boolean } = {};

    joinRoomsNotInGroup(includedRoomIds, id, visitedRooms, wallIndex, onTrueExterior, currentGroup, rooms);
    adjacentRoomGroups.push(currentGroup);
  }

  // group adjacent rooms for determining whether to create an inlet into the area
  for (const group of adjacentRoomGroups) {
    let roomGroupOnTrueExterior = false;

    for (const key in group) {
      if (onTrueExterior[key]) {
        roomGroupOnTrueExterior = true;
        break;
      }
    }

    // if one room in group is on the true exterior, then all rooms are perimeter
    if (roomGroupOnTrueExterior) {
      for (const key in group) {
        roomsPerimeter[key] = true;
      }
    } else {
      for (const key in group) {
        roomsInterior[key] = true;
      }
    }
  }

  return { roomsPerimeter, roomsInterior };
}

//BFS
function joinRoomsNotInGroup(
  includedRoomIds: { [roomId: string]: boolean },
  id: string,
  visitedRooms: { [id: string]: boolean },
  wallIndex: WallIndex,
  onTrueExterior: { [id: string]: boolean },
  currentGroup: { [id: string]: boolean },
  rooms: Table<Room>,
) {
  visitedRooms[id] = true;
  currentGroup[id] = true;

  const visitNext: string[] = [];

  const walls = wallIndex.wallsInRoom(rooms[id]);
  for (const wall of walls) {
    const wallPath = getWallPath(wall);
    const checkLeftRoom = wallPath.rightRoom && wallPath.rightRoom.id === id;
    const oppositeRoom = checkLeftRoom ? wallPath.leftRoom : wallPath.rightRoom;

    if (oppositeRoom === undefined) {
      onTrueExterior[id] = true;
      continue;
    }

    if (includedRoomIds[oppositeRoom.id] || visitedRooms[oppositeRoom.id]) {
      continue;
    }

    visitNext.push(oppositeRoom.id);
  }

  for (const visitId of visitNext) {
    joinRoomsNotInGroup(includedRoomIds, visitId, visitedRooms, wallIndex, onTrueExterior, currentGroup, rooms);
  }
}

function getPlanarGraphFacesForExcludedRooms(
  rooms: Table<Room>,
  roomLevelIndices: Table<number>,
  roomsOnLevel: string[],
  graph: PolygonPlanarGraph,
  roomsPerimeter: { [id: string]: boolean },
) {
  return getPlanarGraphFaces(rooms, roomLevelIndices, roomsOnLevel, graph, (room) => roomsPerimeter[room.id]);
}
